import {
  Check,
  Download,
  Link2,
  Loader2,
  Mail,
  Send,
  Smartphone,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import QRCode from 'qrcode';
import { useEffect, useRef, useState } from 'react';
import logoSmall from '@/assets/brand/logo-small.png';
import { toast } from '@/lib/notify';
import { cn } from '@/lib/utils';

import {
  type InviteRecipientInput,
  type SendWorkflowInvitesResult,
  useSendWorkflowInvites,
} from '../api/useSendWorkflowInvites';

// The ONE link for a workflow — QR, Copy link, an emailed invite and a bulk send
// all carry this. Whoever opens it proves a mobile number by OTP, and THAT creates
// or resumes their run; sharing the link creates nothing on its own. There is no
// per-recipient token link any more.
const commonLinkFor = (slug: string) => `${window.location.origin}/wf/${slug}`;

type Mode = 'individual' | 'bulk';

/**
 * ONE person from the uploaded sheet — their email and their number together.
 *
 * This pairing is the whole point. Emails and numbers used to be collected into two
 * separate lists, so a 10-row sheet with both columns produced TWENTY invites: ten
 * email-only people and ten mobile-only people, none of whom existed. One sheet row
 * is one person, so it must stay one object all the way to the database.
 */
interface BulkRow {
  email: string | null;
  phone: string | null;
}

interface NewSessionDrawerProps {
  open: boolean;
  onClose: () => void;
  workflowId: string;
  workflowName: string;
  // The workflow's shared public slug — forms the common /wf/{slug} link + QR.
  publicSlug: string;
}

export function NewSessionDrawer({
  open,
  onClose,
  workflowId,
  workflowName,
  publicSlug,
}: NewSessionDrawerProps) {
  const [mode, setMode] = useState<Mode>('individual');

  // Reference ID (shared field, shown in both modes per design).
  const [referenceId, setReferenceId] = useState('');
  const [whatsApp, setWhatsApp] = useState(true);

  // Individual
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [qr, setQr] = useState<string | null>(null);

  // The common shared link + its QR are static per workflow (no session created).
  const commonLink = publicSlug ? commonLinkFor(publicSlug) : null;

  // Bulk — one entry per sheet row.
  const [bulkRows, setBulkRows] = useState<BulkRow[]>([]);
  const [uploadedFile, setUploadedFile] = useState<string | null>(null);
  const [sendResult, setSendResult] = useState<SendWorkflowInvitesResult | null>(null);

  const fileRef = useRef<HTMLInputElement>(null);

  const sendInvites = useSendWorkflowInvites();

  // Reset everything when the drawer (re)opens.
  useEffect(() => {
    if (!open) return;
    setMode('individual');
    setReferenceId('');
    setWhatsApp(true);
    setEmail('');
    setPhone('');
    setQr(null);
    setBulkRows([]);
    setUploadedFile(null);
    setSendResult(null);
  }, [open]);

  // Render the QR for the common shared link, with the SignCare mark punched into
  // the centre (high error-correction keeps it scannable). Static per workflow.
  // Individual mode only — bulk has no single QR to show.
  useEffect(() => {
    if (!open || !commonLink) {
      setQr(null);
      return;
    }
    let alive = true;
    const canvas = document.createElement('canvas');
    QRCode.toCanvas(canvas, commonLink, {
      width: 220,
      margin: 1,
      errorCorrectionLevel: 'H',
    })
      .then(() => {
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          if (alive) setQr(canvas.toDataURL());
          return;
        }
        const logo = new Image();
        logo.onload = () => {
          const box = canvas.width * 0.24;
          const pos = (canvas.width - box) / 2;
          const pad = box * 0.14;
          // White plate behind the logo so QR modules don't bleed through.
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(pos - pad, pos - pad, box + pad * 2, box + pad * 2);
          ctx.drawImage(logo, pos, pos, box, box);
          if (alive) setQr(canvas.toDataURL());
        };
        // If the logo fails to load, still show the plain QR.
        logo.onerror = () => {
          if (alive) setQr(canvas.toDataURL());
        };
        logo.src = logoSmall;
      })
      .catch(() => {
        if (alive) setQr(null);
      });
    return () => {
      alive = false;
    };
  }, [open, commonLink]);

  if (!open) return null;

  // Both modes do the same thing now — individual is simply a batch of one.
  //
  // Send RECORDS who we asked and emails them the workflow's COMMON link (the same
  // one the QR encodes), tagged with their own tracking code. It creates NO
  // session: the recipient confirms a mobile number by OTP, and that is what
  // creates their run. So the Sessions list only ever shows real runs, and these
  // records show who hasn't turned up yet.
  function send(recipients: InviteRecipientInput[]) {
    if (recipients.length === 0) {
      toast.error('Add at least one email or phone number');
      return;
    }

    sendInvites.mutate(
      { workflowId, recipients, referenceId: referenceId.trim() || null },
      {
        onSuccess: (result) => {
          setSendResult(result);
          // Report the outcome honestly rather than a blanket success — a bounced
          // address, and a recipient who already has the link, both look identical
          // to "ignored us" otherwise.
          if (result.sent === 0 && result.skipped > 0) {
            toast.info(
              result.skipped === 1
                ? 'Already invited to this workflow — no second link was sent'
                : `All ${result.skipped} already have this workflow's link — nothing was sent`,
            );
          } else if (result.failed > 0 || result.skipped > 0) {
            const notes = [
              result.failed > 0 ? `${result.failed} not delivered` : null,
              result.skipped > 0 ? `${result.skipped} already invited` : null,
            ].filter(Boolean);
            toast.warning(`Sent to ${result.sent} of ${result.total} — ${notes.join(', ')}`);
          } else {
            toast.success(
              `Verification link sent to ${result.sent} recipient${result.sent === 1 ? '' : 's'}`,
            );
          }
        },
        onError: () => toast.error('Could not send the verification link'),
      },
    );
  }

  function handleSendIndividual() {
    send([{ email: email.trim() || null, mobile: phone.trim() || null }]);
  }

  // One recipient per sheet row, carrying BOTH contacts — so ten rows create ten
  // invites, not twenty.
  function handleSendBulk() {
    send(bulkRows.map((r) => ({ email: r.email, mobile: r.phone })));
  }

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    // Checked here as well as by the input's `accept`, because a drag-and-drop
    // bypasses `accept` entirely.
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Only .csv files are supported — download the format sheet to start from.');
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = parseSheet(String(reader.result ?? ''));

      // Nothing is imported from a file that doesn't match the format. Partially
      // accepting it is worse than refusing: a silently dropped row means somebody
      // never gets their link and nobody finds out.
      if (!result.ok) {
        toast.error(result.message);
        return;
      }

      // Merge with what's already listed, keyed on the email (or the number when
      // there is no email) so re-uploading the same sheet can't double anyone.
      let added = 0;
      setBulkRows((prev) => {
        const seen = new Set(prev.map(rowKey));
        const merged = [...prev];
        for (const row of result.rows) {
          const key = rowKey(row);
          if (!seen.has(key)) {
            seen.add(key);
            merged.push(row);
            added++;
          }
        }
        return merged;
      });

      setUploadedFile(file.name);
      const duplicates = result.rows.length - added;
      toast.success(
        `Added ${added} recipient${added === 1 ? '' : 's'} from ${file.name}` +
          (duplicates > 0 ? ` — ${duplicates} already listed` : ''),
      );
    };
    reader.readAsText(file);
    e.target.value = '';
  }

  // Back to the import view. The Session Id is never touched: it's a setting for
  // the send, not a recipient.
  function clearAll() {
    setBulkRows([]);
    setUploadedFile(null);
  }

  function downloadCsvFormat() {
    // The sample the operator fills in — two columns only. There is no RefID
    // column: the Session Id is typed once in the box above and applied to the
    // whole batch, so putting it on every row would just be a way to disagree with
    // itself. Columns and order match exactly what handleFile reads back.
    //
    // Filled with yopmail addresses so the sample is usable as-is for a test run
    // (yopmail inboxes need no signup — open mail.yopmail.com and read them).
    const csv = [
      'Email,Phone',
      'signcare.test01@yopmail.com,+91-9000000001',
      'signcare.test02@yopmail.com,+91-9000000002',
      'signcare.test03@yopmail.com,+91-9000000003',
      'signcare.test04@yopmail.com,+91-9000000004',
      'signcare.test05@yopmail.com,+91-9000000005',
      'signcare.test06@yopmail.com,+91-9000000006',
      'signcare.test07@yopmail.com,+91-9000000007',
      'signcare.test08@yopmail.com,+91-9000000008',
      'signcare.test09@yopmail.com,+91-9000000009',
      'signcare.test10@yopmail.com,+91-9000000010',
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'session_import_format.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  const link = commonLink;

  // Send needs a destination: individual → an email or phone; bulk → ≥1 row.
  // The Session Id stays optional throughout.
  const canSend =
    mode === 'individual' ? email.trim() !== '' || phone.trim() !== '' : bulkRows.length > 0;
  const isSending = sendInvites.isPending;

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/40" onClick={onClose} aria-hidden="true" />
      <aside
        className="fixed top-0 end-0 z-50 flex h-dvh w-full flex-col bg-white shadow-elevation-2 animate-in slide-in-from-right-4 duration-200"
        style={{
          maxWidth: mode === 'individual' ? 'clamp(320px,90vw,750px)' : 'clamp(320px,92vw,720px)',
        }}
      >
        {/* Header */}
        <div className="flex shrink-0 items-start justify-between border-b border-grayEF px-6 py-5">
          <div className="flex flex-col gap-1">
            <h2 className="m-0 text-[length:var(--text-20)] font-semibold text-black-logo">
              New Session
            </h2>
            <p className="m-0 text-[length:var(--text-14)] text-gray66">
              Send the secure link to your user via email or message or QR code
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F4F6FA] text-[#4B5563] transition-colors hover:bg-[#ECEEF3]"
          >
            <X className="h-[18px] w-[18px]" />
          </button>
        </div>

        {/* Body */}
        <div className="custom-scrollbar flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto p-6">
          {/* Mode selector */}
          <div className="flex items-center gap-6">
            {(['individual', 'bulk'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                className={cn(
                  'flex select-none items-center gap-2 text-[length:var(--text-16)] font-medium transition-colors',
                  mode === m ? 'text-black' : 'text-gray66',
                )}
              >
                <span
                  className={cn(
                    'flex h-4 w-4 items-center justify-center rounded-full border',
                    mode === m ? 'border-primary' : 'border-gray-400',
                  )}
                >
                  {mode === m && <span className="h-2 w-2 rounded-full bg-primary" />}
                </span>
                {m === 'individual' ? 'Send to Individual' : 'Send in Bulk'}
              </button>
            ))}
          </div>

          <p className="-mt-3 text-[length:var(--text-12)] text-gray66">
            Workflow: <span className="font-semibold text-gray30">{workflowName}</span>
          </p>

          {mode === 'individual' ? (
            /* ── Individual: contact fields beside the common link's QR ───────── */
            <div className="grid grid-cols-1 items-stretch gap-3 md:grid-cols-[1fr_auto_1fr]">
              {/* Session Id sits INSIDE this column, so it is the same width as the
                  fields it belongs with. Full width here would make one field span
                  the QR as well, which reads as a heading for the whole drawer.
                  Bulk does the opposite for the opposite reason — see below. */}
              <div className="flex flex-col gap-4">
                <SessionIdField
                  value={referenceId}
                  onChange={setReferenceId}
                  placeholder="REF123654897"
                />

                <Fs label="Email Address">
                  <input
                    type="email"
                    className="w-full border-none bg-transparent p-0 text-[length:var(--text-14)] text-gray66 outline-none placeholder:text-placeholder"
                    placeholder="youremail@domain.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </Fs>

                <Fs label="Phone Number">
                  <div className="flex items-center justify-between gap-3">
                    <input
                      className="w-full border-none bg-transparent p-0 text-[length:var(--text-14)] text-gray66 outline-none placeholder:text-placeholder"
                      placeholder="+91-9632597410"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                    <ChannelToggle whatsApp={whatsApp} onToggle={() => setWhatsApp((v) => !v)} />
                  </div>
                </Fs>

                {/* Directly under the last field. It used to be pushed to the bottom
                    of a full-height column, which left a large gap whenever the QR
                    beside it was the taller of the two. */}
                <SendButton
                  onClick={handleSendIndividual}
                  disabled={!canSend || isSending}
                  isSending={isSending}
                  className="mt-1 self-start"
                />
              </div>

              {/* Divider */}
              <div className="hidden flex-col items-center justify-center self-stretch px-2 md:flex">
                <div className="w-px flex-1 bg-[#CBD5E1]" />
                <span className="my-4 text-[length:var(--text-12)] font-semibold uppercase tracking-wider text-[#A5A5A5]">
                  OR
                </span>
                <div className="w-px flex-1 bg-[#CBD5E1]" />
              </div>

              {/* Scan QR Code — the one common link for this workflow. Sharing it
                  creates NO session; a session appears only when someone opens it
                  and proves a mobile number. */}
              <Fs label="Scan QR Code" className="flex h-full flex-col justify-center">
                <div className="flex h-full min-h-[200px] flex-col items-center justify-center py-4 text-center">
                  <div className="mb-4 flex h-[140px] w-[140px] items-center justify-center bg-white p-2">
                    {qr ? (
                      <img src={qr} alt="Scan QR Code" className="h-full w-full object-contain" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center rounded-lg bg-grayEF/40 px-3">
                        <Loader2 className="h-5 w-5 animate-spin text-placeholder" />
                      </div>
                    )}
                  </div>
                  <p className="m-0 mb-3 max-w-[280px] text-[length:var(--text-14)] leading-relaxed text-gray66">
                    Share this link or ask the user to scan the QR code with their mobile device to
                    complete verification in their browser.
                  </p>
                  <LinkChip link={link} />
                </div>
              </Fs>
            </div>
          ) : (
            /* ── Bulk: one full-width column ──────────────────────────────────
               Session Id spans the whole drawer here because it genuinely applies
               to the whole batch — the same reason it is only column-wide in
               individual mode, where it belongs to one recipient. The two layouts
               are deliberately NOT shared. */
            <div className="flex flex-col gap-4">
              <SessionIdField
                value={referenceId}
                onChange={setReferenceId}
                placeholder="REF123654897 (applies to all)"
              />

              {bulkRows.length === 0 ? (
                <ImportPanel
                  onBrowse={() => fileRef.current?.click()}
                  onDownload={downloadCsvFormat}
                  fileInput={fileRef}
                />
              ) : (
                <div className="flex flex-col gap-3">
                  <RecipientsTable
                    rows={bulkRows}
                    onRemove={(index) => {
                      const next = bulkRows.filter((_, i) => i !== index);
                      setBulkRows(next);
                      // Emptying it row by row lands back on the import view, same
                      // as Clear all — otherwise you'd be left with an empty table.
                      if (next.length === 0) setUploadedFile(null);
                    }}
                  />

                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <span className="text-[length:var(--text-12)] text-gray66">
                      {bulkRows.length} recipient{bulkRows.length === 1 ? '' : 's'}
                      {uploadedFile ? ` · ${uploadedFile}` : ''}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-grayEF px-3 py-1.5 text-[length:var(--text-12)] font-medium text-primary transition-colors hover:border-primary hover:bg-[#F4F6FD]"
                      >
                        <Upload className="h-3.5 w-3.5" />
                        Add more
                      </button>
                      <button
                        type="button"
                        onClick={clearAll}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-grayEF px-3 py-1.5 text-[length:var(--text-12)] font-medium text-destructive transition-colors hover:border-destructive hover:bg-destructive/5"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Clear all
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-3 border-t border-grayEF pt-4">
                    <ChannelToggle whatsApp={whatsApp} onToggle={() => setWhatsApp((v) => !v)} />
                    <SendButton
                      onClick={handleSendBulk}
                      disabled={!canSend || isSending}
                      isSending={isSending}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Sent confirmation — ONE common link went to every recipient, so we list
              who it went to rather than a link each. Each of them proves a mobile
              number by OTP, and that is what creates their own run. Shown for both
              modes: an individual send is simply a batch of one. */}
          {sendResult && sendResult.total > 0 && (
            <div className="flex flex-col gap-2 rounded-lg border border-grayEF p-4">
              <p className="m-0 flex flex-wrap items-center gap-1.5 text-[length:var(--text-14)] font-semibold text-black-logo">
                <Check className="h-4 w-4 text-[#16A34A]" />
                Sent to {sendResult.sent} recipient{sendResult.sent === 1 ? '' : 's'}
                {sendResult.failed > 0 && (
                  <span className="font-normal text-destructive">
                    · {sendResult.failed} not delivered
                  </span>
                )}
                {/* Skipped is not a failure — say plainly that they already have it,
                    so a short "sent" count isn't read as something going wrong. */}
                {sendResult.skipped > 0 && (
                  <span className="font-normal text-gray66">
                    · {sendResult.skipped} already invited
                  </span>
                )}
              </p>
              <p className="m-0 text-[length:var(--text-12)] text-gray66">
                Everyone received the same verification link. They each confirm their mobile number,
                which starts their own run — so they appear under “Invited · Not started” until they
                do.
              </p>
              {link && <LinkChip link={link} />}
              <div className="custom-scrollbar flex max-h-[180px] flex-col gap-1 overflow-y-auto">
                {sendResult.invites.map((i) => (
                  <span
                    key={i.id}
                    className={cn(
                      'text-[length:var(--text-12)]',
                      i.sendFailed ? 'text-destructive' : 'text-gray66',
                    )}
                  >
                    {i.email ?? i.mobile}
                    {i.sendFailed && ' — not delivered'}
                    {!i.email && i.mobile && ' — recorded (SMS not sent yet)'}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={handleFile}
        />
      </aside>
    </>
  );
}

/**
 * The optional "Session Id". One component, placed differently by each mode — the
 * WIDTH is the mode's business, the field is not.
 *
 * Labelled "Session Id" for users; the field and column stay `referenceId` /
 * `reference_id`, because `session_id` already means the FK to sessions.id in
 * session_steps and verified_identity, and reusing the name would make it ambiguous.
 *
 * Left blank, the invite mints its own id — so it is always optional.
 */
function SessionIdField({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <Fs label="Session Id">
      <input
        className="w-full border-none bg-transparent p-0 text-[length:var(--text-14)] text-gray66 outline-none placeholder:text-placeholder"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </Fs>
  );
}

/** Dedupe key: the email identifies a person, falling back to the number. */
function rowKey(row: BulkRow) {
  return (row.email ?? row.phone ?? '').toLowerCase();
}

/** The two columns of the format sheet, in the order Download produces them. */
const SHEET_COLUMNS = ['email', 'phone'] as const;

/** Deliberately loose — the mail server is the real judge of an address. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Same rule as the backend's ApplicantMobileNumber: an Indian mobile, with an
 * optional +91 / 91 / 0 prefix and any punctuation. Returns the bare 10 digits, or
 * null when it isn't one — so the sheet is rejected here rather than half-accepted
 * and then rejected by the server.
 */
function validMobile(raw: string): string | null {
  const digits = raw.replace(/\D/g, '');
  const bare =
    digits.length === 12 && digits.startsWith('91')
      ? digits.slice(2)
      : digits.length === 11 && digits.startsWith('0')
        ? digits.slice(1)
        : digits;
  return bare.length === 10 && /^[6-9]/.test(bare) ? bare : null;
}

type SheetResult = { ok: true; rows: BulkRow[] } | { ok: false; message: string };

/**
 * Reads OUR format and nothing else: an `Email,Phone` header followed by one person
 * per line.
 *
 * Strict on purpose. The parser used to guess — classifying any cell containing "@"
 * as an address and any cell with enough digits as a number — which meant a
 * spreadsheet of something else entirely imported as plausible-looking nonsense.
 * A malformed sheet now fails loudly, naming the line, so it can be fixed before
 * anything is sent.
 *
 * The columns may be in either order (a spreadsheet reorder is not an error), and
 * a cell may be blank as long as the row still has one way to reach the person.
 */
function parseSheet(text: string): SheetResult {
  const lines = text.split(/\r?\n/);
  const cells = (line: string) => line.split(/[,;\t]/).map((c) => c.trim().replace(/^"|"$/g, ''));

  const headerIndex = lines.findIndex((l) => l.trim() !== '');
  if (headerIndex < 0) {
    return { ok: false, message: 'That file is empty.' };
  }

  const header = cells(lines[headerIndex] ?? '')
    .filter(Boolean)
    .map((c) => c.toLowerCase());

  const matchesFormat =
    header.length === SHEET_COLUMNS.length && SHEET_COLUMNS.every((c) => header.includes(c));

  if (!matchesFormat) {
    return {
      ok: false,
      message:
        'This file does not match our format. It needs one header row of exactly “Email,Phone” — download the format sheet and fill that in.',
    };
  }

  const emailAt = header.indexOf('email');
  const phoneAt = header.indexOf('phone');
  const rows: BulkRow[] = [];

  for (let i = headerIndex + 1; i < lines.length; i++) {
    const line = lines[i] ?? '';
    if (line.trim() === '') {
      continue;
    }

    // Line numbers are 1-based and count the header, so they match what the
    // operator sees in Excel.
    const lineNo = i + 1;
    const row = cells(line);

    if (row.length > SHEET_COLUMNS.length) {
      return {
        ok: false,
        message: `Line ${lineNo} has ${row.length} columns — our format has exactly two: Email and Phone.`,
      };
    }

    const rawEmail = (row[emailAt] ?? '').trim();
    const rawPhone = (row[phoneAt] ?? '').trim();

    if (!rawEmail && !rawPhone) {
      return {
        ok: false,
        message: `Line ${lineNo} is missing both the email and the phone number — a recipient needs at least one.`,
      };
    }

    if (rawEmail && !EMAIL_PATTERN.test(rawEmail)) {
      return { ok: false, message: `Line ${lineNo}: “${rawEmail}” is not a valid email address.` };
    }

    if (rawPhone && !validMobile(rawPhone)) {
      return {
        ok: false,
        message: `Line ${lineNo}: “${rawPhone}” is not a valid mobile number (10 digits starting 6-9, +91 optional).`,
      };
    }

    rows.push({ email: rawEmail || null, phone: rawPhone || null });
  }

  if (rows.length === 0) {
    return { ok: false, message: 'That sheet has the right header but no recipients under it.' };
  }

  return { ok: true, rows };
}

/**
 * The empty state of a bulk send: get the template, or drop a filled-in sheet.
 * Exactly two actions — there is nothing else to decide at this point, and the
 * table replaces this panel the moment a file lands.
 */
function ImportPanel({
  onBrowse,
  onDownload,
  fileInput,
}: {
  onBrowse: () => void;
  onDownload: () => void;
  fileInput: React.RefObject<HTMLInputElement | null>;
}) {
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: the Upload button below is the keyboard path
    // biome-ignore lint/a11y/noStaticElementInteractions: click-to-browse dropzone
    <div
      className="flex flex-col items-center justify-center gap-4 rounded-xl border border-dashed border-[#C7D2E4] bg-[#F8FAFF] px-6 py-10 text-center transition-colors hover:border-primary hover:bg-[#F4F6FD]"
      onClick={onBrowse}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        const f = e.dataTransfer.files?.[0];
        if (f && fileInput.current) {
          const dt = new DataTransfer();
          dt.items.add(f);
          fileInput.current.files = dt.files;
          fileInput.current.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }}
    >
      <CsvIcon />
      <div className="flex flex-col gap-1">
        <h4 className="m-0 text-[length:var(--text-16)] font-semibold text-black-logo">
          Upload your recipient sheet
        </h4>
        {/* Says the rule up front, because the upload enforces it strictly and a
            refusal after the fact is a worse way to learn it. */}
        <p className="m-0 text-[length:var(--text-12)] text-gray66">
          Drag a .csv here, or use the buttons below. It must be our format — an{' '}
          <span className="font-semibold text-gray30">Email,Phone</span> header, then one person per
          row with at least one of the two.
        </p>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-2.5">
        {/* stopPropagation: the whole panel is a click-to-browse target, so Download
            would otherwise open the file picker as well. */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDownload();
          }}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#00A92C] px-4 py-2 text-[length:var(--text-14)] font-semibold text-white transition-colors hover:bg-[#15803D]"
        >
          <Download className="h-4 w-4" />
          Download format
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onBrowse();
          }}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-[length:var(--text-14)] font-semibold text-white transition-colors hover:bg-[#003be0]"
        >
          <Upload className="h-4 w-4" />
          Upload CSV
        </button>
      </div>
    </div>
  );
}

/**
 * The imported sheet, one line per person.
 *
 * A table rather than chips because email and number belong to the SAME row and
 * must read that way — chips in two boxes is exactly the shape that made ten people
 * look like twenty. Scrolls inside its own box, so a 500-row import never pushes
 * Send off the screen.
 */
function RecipientsTable({
  rows,
  onRemove,
}: {
  rows: BulkRow[];
  onRemove: (index: number) => void;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-[#E5E7EB]">
      <div className="custom-scrollbar max-h-[264px] overflow-y-auto">
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-10 bg-[#F4F6FD] text-left text-[#041A3A]">
            <tr>
              <th className="w-10 px-3 py-2 text-[length:var(--text-12)] font-medium">#</th>
              <th className="px-3 py-2 text-[length:var(--text-12)] font-medium">Email</th>
              <th className="px-3 py-2 text-[length:var(--text-12)] font-medium">Phone Number</th>
              <th className="w-12 px-3 py-2" aria-label="Remove" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr
                key={rowKey(row) || index}
                className="border-t border-grayEF transition-colors hover:bg-[#F8FAFF]"
              >
                <td className="px-3 py-2 text-[length:var(--text-12)] text-placeholder">
                  {index + 1}
                </td>
                {/* An em dash rather than a blank cell: "this person has no email"
                    is information, and a gap reads as a rendering fault. */}
                <td className="max-w-[240px] px-3 py-2">
                  {row.email ? (
                    <span className="flex items-center gap-1.5 text-[length:var(--text-12)] text-black-logo">
                      <Mail className="h-3 w-3 shrink-0 text-gray66" />
                      <span className="truncate" title={row.email}>
                        {row.email}
                      </span>
                    </span>
                  ) : (
                    <span className="text-[length:var(--text-12)] text-placeholder">—</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  {row.phone ? (
                    <span className="flex items-center gap-1.5 text-[length:var(--text-12)] text-black-logo">
                      <Smartphone className="h-3 w-3 shrink-0 text-gray66" />
                      <span className="truncate" title={row.phone}>
                        {row.phone}
                      </span>
                    </span>
                  ) : (
                    <span className="text-[length:var(--text-12)] text-placeholder">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => onRemove(index)}
                    aria-label={`Remove ${row.email ?? row.phone ?? 'recipient'}`}
                    title="Remove this recipient"
                    className="inline-flex h-6 w-6 items-center justify-center rounded-md text-[#94A3B8] transition-colors hover:bg-destructive/10 hover:text-destructive"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * The primary action, shared by both modes so they can't drift apart.
 * The spinner REPLACES the icon rather than joining it, so the button doesn't
 * change width mid-send.
 */
function SendButton({
  onClick,
  disabled,
  isSending,
  className,
}: {
  onClick: () => void;
  disabled: boolean;
  isSending: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={disabled && !isSending ? 'Add a recipient first' : undefined}
      className={cn(
        'inline-flex w-auto shrink-0 items-center justify-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-[length:var(--text-14)] font-semibold text-white transition-colors hover:bg-[#003be0] disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
    >
      {isSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
      Send
    </button>
  );
}

// Fieldset with a legend that sits on the border — matches the designer's inputs.
function Fs({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <fieldset
      className={cn(
        'm-0 min-w-0 rounded-lg border border-[#E5E7EB] px-4 py-3 focus-within:border-primary',
        className,
      )}
    >
      <legend className="px-1.5 text-[length:var(--text-14)] text-black">{label}</legend>
      {children}
    </fieldset>
  );
}

/**
 * The Sms ↔ WhatsApp switch. Extracted because it sits beside the input in
 * individual mode but in the actions row in bulk, and duplicating it is how the two
 * would drift apart.
 */
function ChannelToggle({ whatsApp, onToggle }: { whatsApp: boolean; onToggle: () => void }) {
  return (
    <div className="flex shrink-0 select-none items-center gap-1.5">
      <span
        className={cn(
          'text-[10px] font-medium transition-colors',
          whatsApp ? 'text-[#A5A5A5]' : 'text-gray30',
        )}
      >
        Sms
      </span>
      <button
        type="button"
        onClick={onToggle}
        aria-label="Toggle SMS or WhatsApp"
        className={cn(
          'relative h-[18px] w-8 rounded-full p-0 transition-colors',
          whatsApp ? 'bg-[#16A34A]' : 'bg-gray-200',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 left-0.5 h-3.5 w-3.5 rounded-full bg-white transition-transform',
            whatsApp && 'translate-x-3.5',
          )}
        />
      </button>
      <span
        className={cn(
          'text-[10px] font-medium transition-colors',
          whatsApp ? 'text-gray30' : 'text-[#A5A5A5]',
        )}
      >
        WhatsApp
      </span>
    </div>
  );
}

function LinkChip({ link }: { link: string | null }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    if (!link) return;
    if (!navigator.clipboard) {
      toast.error('Clipboard not available');
      return;
    }
    navigator.clipboard
      .writeText(link)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => toast.error('Could not copy link'));
  }
  return (
    <button
      type="button"
      onClick={copy}
      disabled={!link}
      title={link ?? undefined}
      className="flex w-full max-w-full items-center justify-center gap-2 truncate rounded-full bg-[#F4F6FD] px-4 py-3 text-[length:var(--text-14)] font-semibold text-primary transition-colors hover:bg-[#DCE7FF] disabled:opacity-60"
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 shrink-0 text-[#16A34A]" />
      ) : (
        <Link2 className="h-3.5 w-3.5 shrink-0" />
      )}
      <span className="truncate">{copied ? 'Copied Link!' : (link ?? 'Generating link…')}</span>
    </button>
  );
}

function CsvIcon() {
  return (
    <svg
      width="31"
      height="32"
      viewBox="0 0 31 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M31 8.9955V27.986C31 29.0463 30.5334 30.0632 29.7029 30.813C28.8724 31.5628 27.746 31.984 26.5714 31.984H24.3571V29.985H26.5714C27.1587 29.985 27.7219 29.7744 28.1372 29.3995C28.5524 29.0246 28.7857 28.5162 28.7857 27.986V8.9955H24.3571C23.4762 8.9955 22.6314 8.67959 22.0085 8.11726C21.3856 7.55493 21.0357 6.79225 21.0357 5.997V1.999H8.85714C8.26988 1.999 7.70667 2.20961 7.29141 2.58449C6.87615 2.95938 6.64286 3.46783 6.64286 3.998V21.989H4.42857V3.998C4.42857 2.93767 4.89515 1.92076 5.72567 1.17099C6.55619 0.421217 7.68261 0 8.85714 0L21.0357 0L31 8.9955ZM7.78764 29.6672C7.80032 29.9821 7.88587 30.2912 8.03879 30.5746C8.19171 30.8579 8.40862 31.1092 8.67557 31.3123C8.96343 31.5282 9.31624 31.6962 9.734 31.8161C10.1532 31.9374 10.6441 31.998 11.2065 31.998C11.9549 31.998 12.589 31.8927 13.1086 31.6822C13.6311 31.4716 14.029 31.1784 14.3021 30.8026C14.5781 30.4241 14.7161 29.987 14.7161 29.4913C14.7161 29.0435 14.6172 28.6703 14.4194 28.3718C14.2168 28.0722 13.9305 27.8261 13.5891 27.6582C13.1972 27.4618 12.7747 27.3203 12.3358 27.2384L10.9607 26.9505C10.6373 26.8948 10.3316 26.7746 10.0661 26.5987C9.96511 26.5284 9.88368 26.4377 9.82832 26.3339C9.77296 26.2302 9.74521 26.1162 9.74728 26.001C9.74728 25.6892 9.88383 25.4333 10.1569 25.2334C10.4345 25.0308 10.8124 24.9295 11.2906 24.9295C11.6065 24.9295 11.8796 24.9748 12.1099 25.0655C12.323 25.1416 12.5107 25.2663 12.6546 25.4273C12.7909 25.5757 12.8824 25.7533 12.9204 25.943H14.5811C14.5525 25.5361 14.3992 25.1444 14.1382 24.8116C13.8592 24.4521 13.4762 24.1686 13.0311 23.992C12.4876 23.7758 11.8975 23.672 11.3039 23.6882C10.6559 23.6882 10.0831 23.7881 9.58564 23.988C9.08817 24.1866 8.69919 24.4671 8.41871 24.8296C8.13824 25.1934 7.998 25.6192 7.998 26.1069C7.998 26.5094 8.08805 26.8586 8.26814 27.1544C8.45119 27.4516 8.711 27.6962 9.04757 27.8881C9.38414 28.0786 9.78198 28.2206 10.2411 28.3138L11.6095 28.6017C12.0671 28.699 12.4089 28.8276 12.6347 28.9875C12.7446 29.0638 12.8326 29.1628 12.8912 29.2762C12.9498 29.3897 12.9772 29.5141 12.9713 29.6392C12.9763 29.8453 12.9105 30.0478 12.7831 30.2189C12.6404 30.3943 12.4442 30.5284 12.2184 30.6047C11.9719 30.698 11.6671 30.7446 11.3039 30.7446C11.0456 30.7446 10.8094 30.718 10.5954 30.6647C10.3991 30.6171 10.2133 30.5393 10.0462 30.4348C9.89898 30.3485 9.77291 30.2358 9.67579 30.1038C9.57868 29.9718 9.51258 29.8232 9.48157 29.6672H7.78764ZM1.78471 27.3723C1.78471 26.8752 1.86 26.4534 2.01057 26.1069C2.14214 25.7877 2.37467 25.5104 2.67929 25.3093C2.98906 25.1201 3.35724 25.0242 3.73107 25.0355C4.06321 25.0355 4.35698 25.1001 4.61236 25.2294C4.8619 25.3498 5.07019 25.5295 5.21464 25.7491C5.36897 25.9803 5.46006 26.2414 5.48036 26.5107H7.17429V26.3668C7.15959 25.9986 7.06028 25.6372 6.88265 25.3054C6.70502 24.9737 6.45295 24.6789 6.14243 24.4398C5.82571 24.1946 5.45742 24.0097 5.05964 23.8961C4.62795 23.7626 4.17407 23.697 3.71779 23.7022C2.9295 23.7022 2.2571 23.8507 1.70057 24.1479C1.147 24.4438 0.724809 24.8649 0.434 25.4113C0.146143 25.9577 0.00147619 26.61 0 27.3683V28.3638C0 29.1208 0.142452 29.7711 0.427357 30.3148C0.718167 30.8572 1.14036 31.2744 1.69393 31.5662C2.2475 31.8554 2.92212 32 3.71779 32C4.36583 32 4.94524 31.8907 5.456 31.6722C5.96676 31.4536 6.37271 31.1511 6.67386 30.7646C6.97933 30.3677 7.15295 29.9002 7.17429 29.4173V29.2654H5.48257C5.46161 29.5227 5.37196 29.7717 5.22129 29.991C5.07372 30.2033 4.86571 30.3759 4.619 30.4908C4.34181 30.6097 4.03793 30.6692 3.73107 30.6647C3.35705 30.6736 2.98856 30.5818 2.67264 30.4008C2.36913 30.2062 2.13825 29.9329 2.01057 29.6172C1.84872 29.2159 1.7721 28.7907 1.78471 28.3638V27.3723ZM20.0282 31.8461H17.918L14.9553 23.8521H16.9858L18.9698 30.1249H19.0539L21.0202 23.8521H22.9666L20.0282 31.8461Z"
        fill="#1BBF46"
      />
    </svg>
  );
}
